import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Book, User, MapPin, Tag, AlertCircle, Clock } from 'lucide-react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { toast } from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';

const formatTime = (hour, isEnd = false) => {
  const hour12 = hour % 12 || 12;
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour12.toString().padStart(2, '0')}:${isEnd ? '50' : '00'} ${period}`;
};

const formatTimeRange = (startHour, endHour) => {
  const startTime = formatTime(startHour);
  const endTime = formatTime(endHour, true);
  return `${startTime}-${endTime}`;
};

const days = ['S', 'M', 'T', 'W', 'R', 'F', 'A'];

const convertScheduleToString = (scheduleGrid) => {
    if (!Array.isArray(scheduleGrid)) {
        return '';
    }

    // First, collect all selected slots by day
    const daySlots = {};
    
    scheduleGrid.forEach((hourRow, hourIndex) => {
        if (!Array.isArray(hourRow)) return;

        hourRow.forEach((isSelected, dayIndex) => {
            if (isSelected) {
                const day = days[dayIndex];
                if (!daySlots[day]) {
                    daySlots[day] = [];
                }
                daySlots[day].push(hourIndex);
            }
        });
    });

    // Then, combine consecutive slots for each day
    const combinedSlots = [];
    
    Object.entries(daySlots).forEach(([day, hours]) => {
        hours.sort((a, b) => a - b);
        
        let startHour = hours[0];
        let prevHour = startHour;
        
        for (let i = 1; i <= hours.length; i++) {
            const currentHour = hours[i];
            
            // If we're at the end or there's a gap in consecutive hours
            if (i === hours.length || currentHour !== prevHour + 1) {
                // Find existing slot with the same time range
                const timeRange = formatTimeRange(startHour + 8, prevHour + 8);
                let slot = combinedSlots.find(s => s.timeRange === timeRange);
                
                if (slot) {
                    slot.days += day;
                } else {
                    combinedSlots.push({
                        days: day,
                        timeRange: timeRange
                    });
                }
                
                startHour = currentHour;
            }
            prevHour = currentHour;
        }
    });

    // Sort slots by start time
    combinedSlots.sort((a, b) => {
        const timeA = a.timeRange.split('-')[0];
        const timeB = b.timeRange.split('-')[0];
        return timeA.localeCompare(timeB);
    });
    
    // Generate the final schedule string
    const scheduleString = combinedSlots
        .map(slot => `${slot.days} ${slot.timeRange}`)
        .join(', ');

    return scheduleString;
};

const ClassForm = ({ addClass, updateClass, classes, isDarkMode }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useUser();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    professor: '',
    location: '',
    credits: '',
    description: '',
    schedule: Array(10).fill(Array(7).fill(false)),
    semester: '',
    year: new Date().getFullYear()
  });
  const [errors, setErrors] = useState({});

  // Add helper function to get current semester info
  const getCurrentSemesterInfo = () => {
    const now = new Date();
    const month = now.getMonth(); // 0-11
    let semester;
    if (month >= 0 && month <= 4) {        // January - May
      semester = 'Spring';
    } else if (month >= 5 && month <= 7) { // June - August
      semester = 'Summer';
    } else {                               // September - December
      semester = 'Fall';
    }
    return { semester, year: now.getFullYear() };
  };

  // Set initial semester and year when component mounts
  useEffect(() => {
    const { semester, year } = getCurrentSemesterInfo();
    setFormData(prev => ({
      ...prev,
      semester,
      year
    }));
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }

    const fetchClass = async () => {
      if (id) {
        try {
          const response = await authenticatedFetch(
            `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/${id}`
          );
          const classData = await response.json();
          
          // Convert the schedule string back to grid format
          const scheduleGrid = Array(10).fill().map(() => Array(7).fill(false));
          
          if (classData.schedule) {
            // Handle both string and object formats
            const scheduleStr = typeof classData.schedule === 'string' 
              ? classData.schedule 
              : classData.schedule.displayString || '';

            scheduleStr.split(', ').forEach(scheduleItem => {
              if (!scheduleItem) return;
              
              const [daysStr, timeRange] = scheduleItem.split(' ');
              if (!daysStr || !timeRange) return;

              const [startTime] = timeRange.split('-');
              const hour = parseInt(startTime) - 8; // Adjust for 8 AM start
              
              for (let i = 0; i < daysStr.length; i++) {
                const day = daysStr[i];
                const dayIndex = days.indexOf(day);
                if (dayIndex !== -1 && hour >= 0 && hour < 10) {
                  scheduleGrid[hour][dayIndex] = true;
                }
              }
            });
          }

          setFormData({
            ...classData,
            schedule: scheduleGrid,
            // Parse year as integer if it exists
            year: classData.year ? parseInt(classData.year) : new Date().getFullYear()
          });
        } catch (error) {
          console.error('Error fetching class:', error);
          navigate('/classes');
        }
      }
    };

    fetchClass();
  }, [id, isLoaded, isSignedIn, authenticatedFetch, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
    }
  };

  const handleScheduleChange = (hour, day) => {
    setFormData(prevState => ({
      ...prevState,
      schedule: prevState.schedule.map((h, i) =>
        i === hour ? h.map((d, j) => (j === day ? !d : d)) : h
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Class title is required';
    if (!formData.professor.trim()) newErrors.professor = 'Professor name is required';
    if (!formData.location.trim()) newErrors.location = 'Class location is required';
    if (!formData.credits.trim()) newErrors.credits = 'Number of credits is required';
    if (!formData.semester) newErrors.semester = 'Semester is required';
    if (!formData.year) newErrors.year = 'Year is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
        // Get the schedule data as a string
        const scheduleString = convertScheduleToString(formData.schedule);

        // Format the data to match server expectations
        const classData = {
            id: id || `class_${Date.now()}`,
            title: formData.title.trim(),
            professor: formData.professor.trim(),
            location: formData.location.trim(),
            credits: formData.credits.toString(),
            description: formData.description?.trim() || '',
            schedule: {
                displayString: scheduleString,
                raw: scheduleString
            },
            user_id: user?.id,
            semester: formData.semester,
            year: parseInt(formData.year)
        };

        try {
            console.log('Sending class data:', classData);

            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-class',
                {
                    method: id ? 'PUT' : 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(classData),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save class');
            }

            const savedClass = await response.json();
            
            // Ensure the schedule is in the correct format when updating state
            if (savedClass.schedule) {
                savedClass.schedule = typeof savedClass.schedule === 'string'
                    ? { displayString: savedClass.schedule, raw: savedClass.schedule }
                    : savedClass.schedule;
            }
            
            console.log('Saved class response:', savedClass);

            if (id) {
                updateClass(savedClass);
            } else {
                addClass(savedClass);
            }

            toast.success(id ? 'Class updated successfully' : 'Class created successfully');
            navigate('/classes');
        } catch (error) {
            console.error('Error saving class:', error);
            toast.error(error.message || 'Failed to save class');
        }
    }
  };

  const inputClass = `w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary border-theme bg-theme text-theme theme-transition`;
  const labelClass = `block mb-2 font-bold text-theme theme-transition`;

  const hours = [
    '8:00 AM',
    '9:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM',
    '5:00 PM'
  ];

  // Add authentication check in render
  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center">
        <l-grid
          size="40"
          speed="1.5"
          color="var(--primary-color)"
        ></l-grid>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate('/sign-in');
    return null;
  }

  // Update the schedule cell rendering
  const renderScheduleCell = (isSelected, hourIndex, dayIndex) => (
    <td
      key={dayIndex}
      onClick={() => handleScheduleChange(hourIndex, dayIndex)}
      className={`
        border-t border-l border-theme
        transition-all duration-150
        cursor-pointer
        relative
        group
      `}
    >
      <div className={`
        absolute inset-0 m-1
        rounded
        transition-colors duration-150
        ${isSelected 
          ? 'bg-primary hover:bg-primary-hover' 
          : 'bg-secondary hover:bg-primary/50'
        }
      `}>
        {isSelected && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-theme"></div>
          </div>
        )}
      </div>
    </td>
  );

  return (
    <div className="min-h-screen bg-theme theme-transition">
      {/* Header Section */}
      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm mb-4">
            <button
              onClick={() => navigate('/classes')}
              className="text-accent hover:text-theme hover:underline theme-transition"
            >
              Classes
            </button>
            <span className="mx-2 text-accent">/</span>
            <span className="text-theme">
              {id ? 'Edit Class' : 'New Class'}
            </span>
          </nav>

          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-theme">
                {id ? 'Edit Class Details' : 'Create New Class'}
              </h1>
              <p className="text-sm text-accent">
                Fill in the class information below
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-lg border border-theme bg-theme theme-transition">
              <h2 className="text-lg font-semibold mb-4 text-theme">
                Basic Information
              </h2>
              
              <div className="space-y-4">
                {/* Title Input */}
                <div>
                  <label htmlFor="title" className={labelClass}>
                    <Book className="inline-block mr-2" size={20} />
                    Class Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g., Introduction to Computer Science"
                  />
                  {errors.title && <p className="text-red-500 mt-1">{errors.title}</p>}
                </div>

                {/* Semester and Year Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="semester" className={labelClass}>
                      Semester
                    </label>
                    <select
                      id="semester"
                      name="semester"
                      value={formData.semester}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">Select Semester</option>
                      <option value="Fall">Fall</option>
                      <option value="Spring">Spring</option>
                      <option value="Summer">Summer</option>
                    </select>
                    {errors.semester && <p className="text-red-500 mt-1">{errors.semester}</p>}
                  </div>
                  <div>
                    <label htmlFor="year" className={labelClass}>
                      Year
                    </label>
                    <select
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      {Array.from({ length: 5 }, (_, i) => {
                        const year = new Date().getFullYear() + i - 2;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                    {errors.year && <p className="text-red-500 mt-1">{errors.year}</p>}
                  </div>
                </div>

                {/* Professor Input */}
                <div>
                  <label htmlFor="professor" className={labelClass}>
                    <User className="inline-block mr-2" size={20} />
                    Professor
                  </label>
                  <input
                    type="text"
                    id="professor"
                    name="professor"
                    value={formData.professor}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g., Dr. Jane Smith"
                  />
                  {errors.professor && <p className="text-red-500 mt-1">{errors.professor}</p>}
                </div>

                {/* Credits and Location in same row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="credits" className={labelClass}>
                      <Tag className="inline-block mr-2" size={20} />
                      Credits
                    </label>
                    <input
                      type="number"
                      id="credits"
                      name="credits"
                      value={formData.credits}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g., 3"
                      min="0"
                      step="0.5"
                    />
                    {errors.credits && <p className="text-red-500 mt-1">{errors.credits}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="location" className={labelClass}>
                      <MapPin className="inline-block mr-2" size={20} />
                      Location
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g., Room 301"
                    />
                    {errors.location && <p className="text-red-500 mt-1">{errors.location}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="p-6 rounded-lg border border-theme bg-theme theme-transition">
              <h2 className="text-lg font-semibold mb-4 text-theme">
                Description
              </h2>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={`${inputClass} h-[calc(100%-3rem)]`}
                placeholder="Enter class description, syllabus highlights, or any additional notes"
              ></textarea>
            </div>
          </div>

          {/* Schedule Section */}
          <div className="p-6 rounded-lg border border-theme bg-theme theme-transition">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-theme">
                  Class Schedule
                </h2>
                <p className="text-sm text-accent">
                  Click on the time slots to set your class schedule
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-primary border border-theme rounded"></div>
                  <span className="text-sm text-accent">Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-secondary border border-theme rounded"></div>
                  <span className="text-sm text-accent">Available</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-theme">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-secondary">
                      <th className="p-3 text-left font-medium text-sm text-accent w-24"></th>
                      {days.map((day, index) => (
                        <th
                          key={index}
                          className="p-3 text-center font-medium text-sm text-accent border-l border-theme"
                        >
                          <span className="block">{day}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {formData.schedule.map((hour, hourIndex) => (
                      <tr key={hourIndex} className={`
                        hover:bg-secondary
                        ${hourIndex % 2 === 0 ? 'bg-theme' : 'bg-secondary'}
                      `}>
                        <td className="p-3 text-sm font-medium text-accent border-t border-theme">
                          {hours[hourIndex]}
                        </td>
                        {hour.map((isSelected, dayIndex) => renderScheduleCell(isSelected, hourIndex, dayIndex))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 text-sm text-accent">
              <p className="flex items-center gap-2">
                <AlertCircle size={16} />
                Tip: Click multiple cells to select recurring class times
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/classes')}
              className="px-6 py-2 rounded-lg bg-secondary hover:bg-secondary text-theme transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary hover:bg-primary-hover text-theme rounded-lg transition-colors"
            >
              {id ? 'Update Class' : 'Create Class'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ClassForm;
